/**
 * Global zustand store — composes all slices and applies the persist
 * middleware with an explicit whitelist.
 *
 * **Persist whitelist (NOTHING ELSE)**:
 *   - `ui` slice (current tab)
 *   - `books.activeBookId`
 *   - `metaCache.primaryCurrency`
 *
 * Tokens, transactions, categories, recurring defs, notifications, and
 * sync queue items are NEVER persisted. Tokens come back via GIS silent
 * refresh; data slices come back from Sheets bootstrap.
 *
 * Slice composition follows the standalone-creator pattern from
 * `auth.ts` / `sync.ts` so the slices remain testable in isolation.
 */

import { create } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';
import { createAuthSlice, type AuthSlice } from './slices/auth';
import { createBooksSlice, type BooksSlice } from './slices/books';
import {
  createTransactionsSlice,
  type TransactionsSlice,
} from './slices/transactions';
import {
  createCategoriesSlice,
  type CategoriesSlice,
} from './slices/categories';
import { createRecurringSlice, type RecurringSlice } from './slices/recurring';
import {
  createNotificationsSlice,
  type NotificationsSlice,
} from './slices/notifications';
import { createSyncSlice, type SyncSlice } from './slices/sync';
import {
  createAnalyticsCacheSlice,
  type AnalyticsCacheSlice,
} from './slices/analyticsCache';
import { createUiSlice, type UiSlice } from './slices/ui';

export interface MetaCacheState {
  /** ISO 4217 code (TWD/JPY/USD/EUR/...). Mirrors `meta.primary_currency`. */
  primaryCurrency: string;
}
export interface MetaCacheActions {
  setPrimaryCurrency: (code: string) => void;
}
export type MetaCacheSlice = MetaCacheState & MetaCacheActions;

export const initialMetaCacheState: MetaCacheState = {
  primaryCurrency: 'TWD',
};

const createMetaCacheSlice =
  <T extends MetaCacheSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): MetaCacheSlice => ({
    ...initialMetaCacheState,
    setPrimaryCurrency: (code) => set({ primaryCurrency: code } as Partial<T>),
  });

export interface AppStore {
  // Slices kept as nested namespaces so `partialize` can pick whole subtrees.
  auth: AuthSlice;
  books: BooksSlice;
  transactions: TransactionsSlice;
  categories: CategoriesSlice;
  recurring: RecurringSlice;
  notifications: NotificationsSlice;
  sync: SyncSlice;
  analyticsCache: AnalyticsCacheSlice;
  ui: UiSlice;
  metaCache: MetaCacheSlice;
}

/**
 * Build a slice into a namespaced sub-store. Each slice creator gets its
 * own namespaced `set`/`get` so the slice keeps thinking in its own state
 * shape while the parent store sees `{ <namespace>: <SliceState> }`.
 */
function bindSlice<K extends keyof AppStore, S extends AppStore[K]>(
  key: K,
  factory: (
    set: (partial: Partial<S> | ((s: S) => Partial<S>)) => void,
    get: () => S,
  ) => S,
  parentSet: (
    partial: Partial<AppStore> | ((s: AppStore) => Partial<AppStore>),
  ) => void,
  parentGet: () => AppStore,
): S {
  const localSet: (partial: Partial<S> | ((s: S) => Partial<S>)) => void = (
    partial,
  ) => {
    parentSet((parent) => {
      const current = parent[key] as S;
      const patch = typeof partial === 'function' ? partial(current) : partial;
      return { [key]: { ...current, ...patch } } as Partial<AppStore>;
    });
  };
  const localGet = () => parentGet()[key] as S;
  return factory(localSet, localGet);
}

interface PersistedShape {
  ui: { activeTab: string };
  books: { activeBookId: string | null };
  metaCache: { primaryCurrency: unknown };
}

function safeCurrency(raw: unknown): string {
  if (typeof raw !== 'string') return 'TWD';
  const v = raw.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(v)) return 'TWD';
  return v;
}

const persistOptions: PersistOptions<AppStore, PersistedShape> = {
  name: 'mochia.store',
  partialize: (state): PersistedShape => ({
    ui: { activeTab: state.ui.activeTab },
    books: { activeBookId: state.books.activeBookId },
    metaCache: {
      primaryCurrency: state.metaCache.primaryCurrency,
    },
  }),
  // Merge persisted whitelist back into the freshly initialised store.
  merge: (persisted, current): AppStore => {
    const p = (persisted ?? {}) as Partial<PersistedShape>;
    return {
      ...current,
      ui: { ...current.ui, activeTab: p.ui?.activeTab ?? current.ui.activeTab },
      books: {
        ...current.books,
        activeBookId: p.books?.activeBookId ?? current.books.activeBookId,
      },
      metaCache: {
        ...current.metaCache,
        primaryCurrency: safeCurrency(
          p.metaCache?.primaryCurrency ?? current.metaCache.primaryCurrency,
        ),
      },
    };
  },
};

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      auth: bindSlice(
        'auth',
        (s, g) =>
          createAuthSlice(
            s as unknown as Parameters<typeof createAuthSlice>[0],
            g as unknown as Parameters<typeof createAuthSlice>[1],
            {} as unknown as Parameters<typeof createAuthSlice>[2],
          ),
        set,
        get,
      ),
      books: bindSlice('books', (s) => createBooksSlice<BooksSlice>()(s), set, get),
      transactions: bindSlice(
        'transactions',
        (s) => createTransactionsSlice<TransactionsSlice>()(s),
        set,
        get,
      ),
      categories: bindSlice(
        'categories',
        (s, g) => createCategoriesSlice<CategoriesSlice>()(s, g),
        set,
        get,
      ),
      recurring: bindSlice(
        'recurring',
        (s) => createRecurringSlice<RecurringSlice>()(s),
        set,
        get,
      ),
      notifications: bindSlice(
        'notifications',
        (s) => createNotificationsSlice<NotificationsSlice>()(s),
        set,
        get,
      ),
      sync: bindSlice('sync', (s) => createSyncSlice<SyncSlice>()(s), set, get),
      analyticsCache: bindSlice(
        'analyticsCache',
        (s, g) => createAnalyticsCacheSlice<AnalyticsCacheSlice>()(s, g),
        set,
        get,
      ),
      ui: bindSlice('ui', (s) => createUiSlice<UiSlice>()(s), set, get),
      metaCache: bindSlice(
        'metaCache',
        (s) => createMetaCacheSlice<MetaCacheSlice>()(s),
        set,
        get,
      ),
    }),
    persistOptions,
  ),
);

// ─── Rehydrate auth slice from session.ts on cold load ─────────────────────
//
// `session.ts` mirrors the OAuth token to localStorage (with expiresAt) so
// PWA cold-loads on iOS Safari survive without re-login during the natural
// 1-hour token window. The auth slice itself is intentionally NOT in the
// persist whitelist (we don't want zustand-persist's serialiser to touch
// the token), so after a refresh the store starts with `auth.token = null`.
// Without this rehydrate, the user lands on LoginScreen on every refresh
// even though session.ts has a still-valid token.
//
// Sharp edges:
//  - We only rehydrate if `session.getToken()` returns non-null. session.ts
//    drops expired entries on read, so by the time we get here the token is
//    guaranteed to still be within its expiresAt window.
import {
  getToken as readSessionToken,
  getEmail as readSessionEmail,
  getExpiresAt as readSessionExpiresAt,
} from '@/lib/auth/session';

if (typeof window !== 'undefined') {
  const token = readSessionToken();
  if (token) {
    useStore.getState().auth.setSession({
      token,
      email: readSessionEmail(),
      expiresAt: readSessionExpiresAt(),
    });
  }
}
